import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { animate, stagger } from "animejs";
import PatrolSphere from "@/components/PatrolSphere";
import { FAQS } from "@/data/faqs";
import Reveal from "@/components/Reveal";
import approachStack from "@/assets/images/approach-stack.jpg";

const SEATS = {
  product: {
    pain: "Roadmaps outrun the platform. Auth, tenancy, and billing become the real product while features stall in staging.",
    fix: "SaaS architecture, API design, admin surfaces, and a delivery harness that keeps shipping safe.",
    outcome: "A product that compounds — not a prototype that needs another rewrite every quarter.",
  },
  commerce: {
    pain: "Checkout friction, themes that fight conversion, and apps that never match how merchants actually work.",
    fix: "Shopify themes & apps, storefront UX, catalog architecture, and growth-ready integrations.",
    outcome: "A storefront that converts and a merchant workflow that stays operable after launch.",
  },
  ops: {
    pain: "Integrations held together with scripts, observability that arrives too late, and handoffs that erase context.",
    fix: "Clean APIs, CI/CD, documentation, and systems designed for the next engineer — not just the demo.",
    outcome: "Fewer fire drills. Clear ownership. Software that stays shippable.",
  },
  exec: {
    pain: "You’re betting on AI and digital growth off platforms you can’t fully trust to deliver on time.",
    fix: "Senior-owned engagement: scoped outcomes, visible demos, and a foundation that reports and AI can rely on.",
    outcome: "Decisions backed by systems that work — not slide decks that age out after the kickoff.",
  },
};

const STACK = [
  "Ruby on Rails",
  "React",
  "Angular",
  "NestJS",
  "Python",
  "ASP.NET",
  "Shopify",
  "TypeScript",
  "PostgreSQL",
  "Three.js",
  "LLM / Agents",
];

function Arrow() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

export default function LandingPage() {
  const [seat, setSeat] = useState("product");

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;

    const targets = document.querySelectorAll(".hero__brand, .hero__line, .hero__actions");
    animate(targets, {
      opacity: [0, 1],
      y: [28, 0],
      delay: stagger(100, { start: 120 }),
      duration: 850,
      ease: "out(3)",
    });

    const stats = document.querySelectorAll("[data-count]");
    const sio = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          const end = Number(el.dataset.count);
          const suffix = el.dataset.suffix || "";
          const obj = { v: 0 };
          animate(obj, {
            v: end,
            duration: 1400,
            ease: "out(3)",
            onUpdate: () => {
              el.textContent = Math.round(obj.v) + suffix;
            },
          });
          sio.unobserve(el);
        });
      },
      { threshold: 0.45 }
    );
    stats.forEach((s) => sio.observe(s));
    return () => sio.disconnect();
  }, []);

  const active = SEATS[seat];

  return (
    <div className="landing">
      <section className="hero" aria-label="Introduction">
        <PatrolSphere variant="hero" />
        <div className="hero__veil" />
        <div className="hero__content">
          <h1 className="hero__brand">
            <span>IT</span>
            <span>Patrol</span>
          </h1>
          <p className="hero__line">
            A development and consulting agency for ecommerce, SaaS, and AI — delivered into production.
          </p>
          <div className="hero__actions">
            <Link className="btn" to="/contact">
              Start a project
              <Arrow />
            </Link>
          </div>
        </div>
      </section>

      <div className="marquee" aria-hidden="true">
        <div className="marquee__track">
          {[0, 1].map((copy) => (
            <div className="marquee__item" key={copy}>
              Ecommerce platforms<span className="marquee__dot" />
              Shopify &amp; custom storefronts<span className="marquee__dot" />
              SaaS product engineering<span className="marquee__dot" />
              AI systems &amp; agents<span className="marquee__dot" />
              Integrations &amp; data pipelines<span className="marquee__dot" />
              Continuous delivery<span className="marquee__dot" />
            </div>
          ))}
        </div>
      </div>

      <section className="services" id="services">
        <div className="wrap">
          <Reveal className="section__head">
            <p className="eyebrow">01 / Services</p>
            <h2>Three lanes. One standard of craft.</h2>
            <p>Ecommerce, SaaS, and AI — three practices, one bar for craft, velocity, and product judgment.</p>
          </Reveal>
          <div className="service-grid">
            <Reveal as="article" className="service">
              <div className="service__top">
                <h3>Ecommerce</h3>
                <span className="service__idx">01</span>
              </div>
              <div className="service__pain">
                <strong>The pain</strong>
                Storefronts that stall at checkout, themes that fight conversion, and apps that never quite fit the
                merchant’s real workflow.
              </div>
              <div className="service__pain">
                <strong>What we build</strong>
                Shopify themes &amp; apps, custom storefronts, checkout UX, catalog architecture, and growth-ready
                integrations.
              </div>
              <div className="service__tags">
                <span className="tag">Shopify</span>
                <span className="tag">Storefront UX</span>
                <span className="tag">Apps</span>
                <span className="tag">Conversion</span>
              </div>
            </Reveal>
            <Reveal as="article" className="service">
              <div className="service__top">
                <h3>SaaS</h3>
                <span className="service__idx">02</span>
              </div>
              <div className="service__pain">
                <strong>The pain</strong>
                Roadmaps that outpace the platform — brittle auth, unclear multi-tenancy, and features that never leave
                staging.
              </div>
              <div className="service__pain">
                <strong>What we build</strong>
                Multi-tenant products, billing-ready backends, admin surfaces, APIs, and the ops harness that keeps
                shipping safe.
              </div>
              <div className="service__tags">
                <span className="tag">Rails / Nest</span>
                <span className="tag">React</span>
                <span className="tag">APIs</span>
                <span className="tag">Billing</span>
              </div>
            </Reveal>
            <Reveal as="article" className="service">
              <div className="service__top">
                <h3>AI</h3>
                <span className="service__idx">03</span>
              </div>
              <div className="service__pain">
                <strong>The pain</strong>
                Prototypes that impress in a demo and collapse under real data, evals, and the need for a human in the
                loop.
              </div>
              <div className="service__pain">
                <strong>What we build</strong>
                Agent workflows, RAG systems, eval harnesses, and product surfaces where AI is accountable — not
                theatrical.
              </div>
              <div className="service__tags">
                <span className="tag">Agents</span>
                <span className="tag">RAG</span>
                <span className="tag">Evals</span>
                <span className="tag">LLM ops</span>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="audience" id="audience" aria-labelledby="audience-title">
        <div className="wrap">
          <Reveal className="section__head">
            <p className="eyebrow">02 / Who it’s for</p>
            <h2 id="audience-title">Bad software costs every team something different.</h2>
            <p>Find the seat you sit in. The problem changes — the standard of craft does not.</p>
          </Reveal>
          <Reveal className="seat-tabs" role="tablist" aria-label="Audience">
            {[
              ["product", "Product"],
              ["commerce", "Commerce"],
              ["ops", "Ops / Eng"],
              ["exec", "Executive"],
            ].map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={`seat-tab${seat === id ? " is-active" : ""}`}
                role="tab"
                aria-selected={seat === id}
                onClick={() => setSeat(id)}
              >
                {label}
              </button>
            ))}
          </Reveal>
          <Reveal className="seat-panel is-active" role="tabpanel">
            <div className="seat-block">
              <strong>The pain</strong>
              <p>{active.pain}</p>
            </div>
            <div className="seat-block">
              <strong>What fixes it</strong>
              <p>{active.fix}</p>
            </div>
            <div className="seat-block">
              <strong>The outcome</strong>
              <p>{active.outcome}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="story" id="approach">
        <div className="story__grid">
          <div className="story__visual">
            <img
              className="story__image"
              src={approachStack}
              alt="Banded sphere resting on layered glass system plates — existing stack hardened and extended underneath."
              width={1200}
              height={1600}
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="story__copy">
            <p className="eyebrow" style={{ color: "rgba(255,255,255,0.45)" }}>
              03 / Approach
            </p>
            <Reveal as="h2">Underneath your stack — not instead of it.</Reveal>
            <Reveal as="p">
              We keep your DMS, CRM, commerce platform, and tools. We harden what you already run, then extend it with
              systems that stay maintainable.
            </Reveal>
            <div className="steps">
              <Reveal className="step">
                <span className="step__n">01</span>
                <div>
                  <h4>Map the signal</h4>
                  <p>Constraints, revenue levers, and technical debt — named before a line of product code ships.</p>
                </div>
              </Reveal>
              <Reveal className="step">
                <span className="step__n">02</span>
                <div>
                  <h4>Build in the open</h4>
                  <p>Short loops, visible demos, and decisions recorded so the next engineer inherits clarity.</p>
                </div>
              </Reveal>
              <Reveal className="step">
                <span className="step__n">03</span>
                <div>
                  <h4>Leave it operable</h4>
                  <p>Observability, docs, and handoff that mean the product keeps moving after we step back.</p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="proof" id="proof">
        <div className="wrap">
          <Reveal className="section__head">
            <p className="eyebrow">04 / Proof</p>
            <h2>Quality is the product.</h2>
            <p>Customer satisfaction and craft are not slogans — they are the acceptance criteria on every engagement.</p>
          </Reveal>
          <div className="proof__stats">
            <Reveal className="stat">
              <div className="stat__value" data-count="3">
                0
              </div>
              <div className="stat__label">Core practice lanes</div>
            </Reveal>
            <Reveal className="stat">
              <div className="stat__value" data-count="12" data-suffix="+">
                0
              </div>
              <div className="stat__label">Years shipping software</div>
            </Reveal>
            <Reveal className="stat">
              <div className="stat__value" data-count="100" data-suffix="%">
                0
              </div>
              <div className="stat__label">Senior-owned delivery</div>
            </Reveal>
            <Reveal className="stat">
              <div className="stat__value">∞</div>
              <div className="stat__label">Curiosity for better systems</div>
            </Reveal>
          </div>
          <Reveal className="values">
            <h3>Bridge market need and new technology — then ship it.</h3>
            <p>
              We help teams digitalize without theater: pragmatic architecture, honest timelines, and interfaces people
              actually use.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="outcomes" id="outcomes" aria-labelledby="outcomes-title">
        <div className="wrap">
          <Reveal className="section__head">
            <p className="eyebrow">05 / Outcomes</p>
            <h2 id="outcomes-title">Underneath your tools — not instead of them.</h2>
            <p>Keep the platforms you already run. We harden them, connect them, and extend them with systems that stay maintainable.</p>
          </Reveal>
          <div className="outcome-grid">
            <Reveal as="article" className="outcome">
              <h3>Commerce that converts</h3>
              <p>Themes, apps, and storefronts tuned for real checkout paths — not demo catalogs.</p>
            </Reveal>
            <Reveal as="article" className="outcome">
              <h3>SaaS that compounds</h3>
              <p>Multi-tenant foundations, billing-ready APIs, and admin UX your team can operate.</p>
            </Reveal>
            <Reveal as="article" className="outcome">
              <h3>AI that stays accountable</h3>
              <p>Agents and RAG with evals, observability, and a human loop — not demo theater.</p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="stack" id="stack">
        <div className="wrap">
          <Reveal className="section__head" style={{ marginInline: "auto", textAlign: "center", maxWidth: "36rem" }}>
            <p className="eyebrow">06 / Stack</p>
            <h2>Tools we ship with daily.</h2>
          </Reveal>
          <Reveal className="stack__row">
            {STACK.map((item) => (
              <span className="chip" key={item}>
                {item}
              </span>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="entity" id="about" aria-labelledby="about-title">
        <div className="wrap">
          <Reveal as="p" className="eyebrow">
            07 / About
          </Reveal>
          <Reveal as="h2" id="about-title">
            What is IT-Patrol?
          </Reveal>
          <Reveal as="p" className="entity__lead">
            IT-Patrol is a development and consulting agency. We help companies design, build, and operate ecommerce
            platforms, SaaS products, and AI systems — with senior engineers owning delivery from brief to production.
          </Reveal>
          <div className="entity__facts">
            <Reveal className="entity__fact">
              <h3>Entity</h3>
              <p>IT-Patrol — development &amp; consulting agency focused on ecommerce, SaaS, and AI.</p>
            </Reveal>
            <Reveal className="entity__fact">
              <h3>Offers</h3>
              <p>Shopify &amp; custom commerce, multi-tenant SaaS, agent/RAG systems, integrations, and IT consulting.</p>
            </Reveal>
            <Reveal className="entity__fact">
              <h3>How to hire</h3>
              <p>Send a concise brief (problem, outcome, stack, timing). We reply with a clear next step.</p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="faq" id="faq" aria-labelledby="faq-title">
        <div className="wrap">
          <Reveal className="section__head">
            <p className="eyebrow">08 / FAQ</p>
            <h2 id="faq-title">What teams usually ask.</h2>
            <p>Direct answers for humans and answer engines.</p>
          </Reveal>
          <div className="faq-list">
            {FAQS.slice(0, 4).map((item) => (
              <Reveal as="article" className="faq-item" key={item.q}>
                <h3>{item.q}</h3>
                <p>{item.a}</p>
              </Reveal>
            ))}
          </div>
          <Reveal style={{ marginTop: "1.25rem" }}>
            <Link to="/faq" style={{ textDecoration: "underline" }}>
              See all FAQs →
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="contact" id="contact-cta">
        <div className="wrap contact__panel">
          <Reveal>
            <p className="eyebrow">09 / Next</p>
            <h2>Need a team that ships from brief to production?</h2>
            <p>Tell us what you’re building — ecommerce, SaaS, or AI. We’ll reply with a clear next step.</p>
          </Reveal>
          <Reveal>
            <Link className="btn" to="/contact">
              Go to contact
              <Arrow />
            </Link>
            <p className="form__note" style={{ marginTop: "1rem" }}>
              Or explore upcoming product surfaces on the <Link to="/apps">Apps</Link> page.
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
